/*
 * Select multiple items
 *
 * Example:
 *
 * JS:
 * const carousels = datajsAll('carousel-block');
 *
 * carousels.forEach((carousel) => {
 *    this.carouselConfig(carousel);
 * });
 *
 * HTML:
 * <div class="o-carousel__slides" data-js="carousel-block">
 */
export function datajsAll(selector, parent) {
	return Array.prototype.slice.call((parent || document).querySelectorAll('[data-js~="' + selector + '"]'));
}

/*
 * Select first (single) item
 *
 * Example:
 *
 * JS:
 * const headerHeight = datajs('site-header').offsetHeight;
 *
 * HTML:
 * <header class="o-siteHeader" data-js="site-header">
 */
export function datajs(selector, parent) {
	return (parent || document).querySelector('[data-js~="' + selector + '"]');
}

export function slugify(text) {
	return text
		.toLowerCase()
		.replace(/[^\w ]+/g, '')
		.replace(/ +/g, '-');
}

export function disableElements(elements) {
	var length = elements.length;
	while (length--) {
		elements[length].disabled = true;
	}
}

export function enableElements(elements) {
	var length = elements.length;
	while (length--) {
		elements[length].disabled = false;
	}
}

// Detect md and larger screens with JS
export let isBreakpointMdUp = '(min-width: 768px)';

// Detect lg and larger screens with JS
export const isBreakpointLgUp = '(min-width: 992px)';

// Detect lg and larger screens with JS
export const isBreakpointMdDown = '(max-width: 767px)';

// Collapse desktop navigation (top level) open submenu items
export function collapseOpenSubnavs(delay = 500) {
	const siteNav = document.querySelector('.js-unique-subnav');

	if (!siteNav) {
		return;
	}

	const topLevelItems = siteNav.querySelectorAll(':scope > .menu-item');

	topLevelItems.forEach((item) => {
		if (item.getAttribute('aria-expanded') === 'true') {
			item.setAttribute('aria-expanded', 'false');
			item.querySelector('a').removeAttribute('aria-current');
			item.classList.remove('is-active');
			setTimeout(function () {
				item.classList.remove('is-activated');
			}, delay);
		}
	});
}

export function hasScollState() {
	const siteHeader = datajs('site-header');

	if (!siteHeader) {
		return;
	}

	const st = window.pageYOffset || document.documentElement.scrollTop;
	const isScrollingUp = document.body.classList.contains('is-scrollingUp');
	let scrollThreshold = siteHeader.offsetHeight;

	if (isScrollingUp) {
		scrollThreshold = 0;
	}

	// Hide nav if page is reloaded in the middle of the page
	if (st > scrollThreshold) {
		return true;
	}

	return false;
}

export function getMapLocations(args = { lang: 'sv', postTypes: '' }, cb) {
	const apiUrl = window.AppGlobals.apiUrl || null;
	let { lang, postTypes } = args;

	if (typeof postTypes === 'undefined') {
		postTypes = '';
	}

	if (!apiUrl) {
		return false;
	}

	const url = `${apiUrl}pe-api/v1/map-locations?lang=${lang}&post_types=${postTypes}`;

	fetch(url)
		.then((response) => response.json())
		.then((data) => cb(data));
}

// Simple debounce function
export const debounce = (callback, wait = 100) => {
	let timeoutId = null;
	return (...args) => {
		window.clearTimeout(timeoutId);
		timeoutId = window.setTimeout(() => {
			callback.apply(null, args);
		}, wait);
	};
};

// Trap focus helper
export function trapFocus(element) {
	const focusableEls = element.querySelectorAll(
		'a[href]:not([disabled]), button:not([disabled]), textarea:not([disabled]), input[type="text"]:not([disabled]), input[type="radio"]:not([disabled]), input[type="checkbox"]:not([disabled]), select:not([disabled])'
	);
	const firstFocusableEl = focusableEls[0];
	const lastFocusableEl = focusableEls[focusableEls.length - 1];
	const KEYCODE_TAB = 9;

	element.addEventListener('keydown', function (e) {
		const isTabPressed = e.key === 'Tab' || e.keyCode === KEYCODE_TAB;

		if (!isTabPressed) {
			return;
		}

		if (e.shiftKey) {
			/* shift + tab */ if (document.activeElement === firstFocusableEl) {
				lastFocusableEl.focus();
				e.preventDefault();
			}
		} /* tab */ else {
			if (document.activeElement === lastFocusableEl) {
				firstFocusableEl.focus();
				e.preventDefault();
			}
		}
	});
}

// Map utilities
export function getMapStyle() {
	return [
		{
			featureType: 'water',
			elementType: 'geometry',
			stylers: [
				{
					color: '#e9e9e9',
				},
				{
					lightness: 17,
				},
			],
		},
		{
			featureType: 'landscape',
			elementType: 'geometry',
			stylers: [
				{
					color: '#f5f5f5',
				},
				{
					lightness: 20,
				},
			],
		},
		{
			featureType: 'road.highway',
			elementType: 'geometry.fill',
			stylers: [
				{
					color: '#ffffff',
				},
				{
					lightness: 17,
				},
			],
		},
		{
			featureType: 'road.highway',
			elementType: 'geometry.stroke',
			stylers: [
				{
					color: '#ffffff',
				},
				{
					lightness: 29,
				},
				{
					weight: 0.2,
				},
			],
		},
		{
			featureType: 'road.arterial',
			elementType: 'geometry',
			stylers: [
				{
					color: '#ffffff',
				},
				{
					lightness: 18,
				},
			],
		},
		{
			featureType: 'road.local',
			elementType: 'geometry',
			stylers: [
				{
					color: '#ffffff',
				},
				{
					lightness: 16,
				},
			],
		},
		{
			featureType: 'poi',
			elementType: 'geometry',
			stylers: [
				{
					color: '#f5f5f5',
				},
				{
					lightness: 21,
				},
			],
		},
		{
			featureType: 'poi.park',
			elementType: 'geometry',
			stylers: [
				{
					color: '#dedede',
				},
				{
					lightness: 21,
				},
			],
		},
		{
			elementType: 'labels.text.stroke',
			stylers: [
				{
					visibility: 'on',
				},
				{
					color: '#ffffff',
				},
				{
					lightness: 16,
				},
			],
		},
		{
			elementType: 'labels.text.fill',
			stylers: [
				{
					saturation: 36,
				},
				{
					color: '#333333',
				},
				{
					lightness: 40,
				},
			],
		},
		{
			elementType: 'labels.icon',
			stylers: [
				{
					visibility: 'off',
				},
			],
		},
		{
			featureType: 'transit',
			elementType: 'geometry',
			stylers: [
				{
					color: '#f2f2f2',
				},
				{
					lightness: 19,
				},
			],
		},
		{
			featureType: 'administrative',
			elementType: 'geometry.fill',
			stylers: [
				{
					color: '#fefefe',
				},
				{
					lightness: 20,
				},
			],
		},
		{
			featureType: 'administrative',
			elementType: 'geometry.stroke',
			stylers: [
				{
					color: '#fefefe',
				},
				{
					lightness: 17,
				},
				{
					weight: 1.2,
				},
			],
		},
	];
}

export function getMapIcon(type, args = { activeIcon: false }) {
	const officeIconActive = `<svg width="50" height="67" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M50 25.19c0 11.467-15.234 31.881-21.914 40.304a3.915 3.915 0 0 1-6.172 0C15.117 57.073 0 36.657 0 25.19 0 11.278 11.193 0 25 0c13.802 0 25 11.278 25 25.19Z" fill="#C62774"/><path d="M30.209 23.029a3.985 3.985 0 1 0 0-7.97 3.985 3.985 0 0 0 0 7.97Zm0-6.661a2.676 2.676 0 1 1 0 5.352 2.676 2.676 0 0 1 0-5.352ZM36.02 25.94a6.647 6.647 0 0 0-4.71-1.96h-2.201a6.693 6.693 0 0 0-3.36 12.48v2.924a2.081 2.081 0 0 0 2.081 2.081h4.758a2.082 2.082 0 0 0 2.081-2.081V36.46A6.7 6.7 0 0 0 38 30.671a6.632 6.632 0 0 0-1.98-4.732Zm-1.351 8.936v-5.484a.654.654 0 1 0-1.308 0v9.992a.773.773 0 0 1-.774.773H27.83a.773.773 0 0 1-.774-.773v-9.992a.654.654 0 0 0-.654-.654h-.053c-.302 0-.654.286-.654.654v5.477c-1.273-1.05-2.023-2.594-2.023-4.198a5.312 5.312 0 0 1 1.606-3.806 5.424 5.424 0 0 1 3.83-1.576h2.201a5.383 5.383 0 0 1 3.36 9.587ZM18.79 22.018a3.508 3.508 0 1 0 0-7.017 3.508 3.508 0 0 0 0 7.017Zm0-5.71a2.2 2.2 0 1 1 0 4.4 2.2 2.2 0 0 1 0-4.4Z" fill="#fff"/><path d="m22.27 35.4-.328-.503v3.476a.773.773 0 0 1-.773.773H16.41a.773.773 0 0 1-.773-.773V28.38a.654.654 0 0 0-1.309 0v5.49a5.386 5.386 0 0 1 3.361-9.593h2.2c1.043 0 2.062.302 2.937.87l.128.082.101-.113c.212-.233.437-.454.675-.66l.17-.15-.186-.13a6.633 6.633 0 0 0-3.824-1.208h-2.2a6.694 6.694 0 0 0-3.361 12.48v2.924a2.082 2.082 0 0 0 2.081 2.081h4.758a2.082 2.082 0 0 0 2.082-2.081v-1.76l-.049-.052a8.057 8.057 0 0 1-.933-1.162Z" fill="#fff"/></svg>`;
	const officeIcon = `<svg width="50" height="67" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="m22.305 65.183-.002-.003c-3.39-4.202-8.855-11.392-13.468-18.957C6.528 42.441 4.44 38.575 2.93 34.95 1.416 31.314.5 27.959.5 25.19.5 11.55 11.472.5 25 .5c13.522 0 24.5 11.05 24.5 24.69 0 2.77-.923 6.123-2.446 9.758-1.52 3.626-3.619 7.492-5.933 11.275-4.627 7.564-10.091 14.755-13.426 18.96a3.416 3.416 0 0 1-5.39 0Z" fill="#faeff4" stroke="#C62774"/><path d="M30.209 23.029a3.985 3.985 0 1 0 0-7.97 3.985 3.985 0 0 0 0 7.97Zm0-6.661a2.676 2.676 0 1 1 0 5.352 2.676 2.676 0 0 1 0-5.352ZM36.02 25.94a6.649 6.649 0 0 0-4.71-1.96h-2.201a6.693 6.693 0 0 0-3.36 12.48v2.924a2.081 2.081 0 0 0 2.081 2.081h4.758a2.082 2.082 0 0 0 2.081-2.081V36.46A6.7 6.7 0 0 0 38 30.671a6.632 6.632 0 0 0-1.98-4.732Zm-1.351 8.936v-5.484a.654.654 0 1 0-1.308 0v9.992a.773.773 0 0 1-.774.773H27.83a.773.773 0 0 1-.774-.773v-9.992a.654.654 0 0 0-.654-.654h-.053c-.302 0-.654.286-.654.654v5.477c-1.273-1.05-2.023-2.594-2.023-4.198a5.312 5.312 0 0 1 1.606-3.806 5.424 5.424 0 0 1 3.83-1.576h2.201a5.383 5.383 0 0 1 3.36 9.587ZM18.79 22.018a3.508 3.508 0 1 0 0-7.017 3.508 3.508 0 0 0 0 7.017Zm0-5.71a2.2 2.2 0 1 1 0 4.4 2.2 2.2 0 0 1 0-4.4Z" fill="#C62774"/><path d="m22.27 35.4-.328-.503v3.476a.773.773 0 0 1-.773.773H16.41a.773.773 0 0 1-.773-.773V28.38a.654.654 0 0 0-1.309 0v5.49a5.386 5.386 0 0 1 3.361-9.593h2.2c1.043 0 2.062.302 2.937.87l.128.082.101-.113c.212-.233.437-.454.675-.66l.17-.15-.186-.131a6.634 6.634 0 0 0-3.824-1.207h-2.2a6.694 6.694 0 0 0-3.361 12.48v2.924a2.082 2.082 0 0 0 2.081 2.081h4.758a2.082 2.082 0 0 0 2.082-2.081v-1.76l-.049-.052a8.06 8.06 0 0 1-.933-1.162Z" fill="#C62774"/></svg>`;
	const caseIconActive = `<svg width="50" height="67" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M50 25.19c0 11.467-15.234 31.881-21.914 40.304a3.915 3.915 0 0 1-6.172 0C15.117 57.073 0 36.657 0 25.19 0 11.278 11.193 0 25 0c13.802 0 25 11.278 25 25.19Z" fill="#C62774"/><path d="M35.32 41.001H14.68a.673.673 0 0 1-.48-.203.7.7 0 0 1-.2-.49V21.422a.7.7 0 0 1 .2-.49c.127-.131.3-.204.48-.204h4.76v-9.034a.705.705 0 0 1 .277-.56.672.672 0 0 1 .606-.102l15.2 4.82a.68.68 0 0 1 .344.252c.086.119.132.263.132.41v23.794a.7.7 0 0 1-.199.49.673.673 0 0 1-.48.203Zm-9.17-1.387h8.49V17.026l-13.839-4.391v8.093h4.669c.18 0 .353.073.48.203a.7.7 0 0 1 .2.49v18.193Zm-10.79 0h9.43V22.115h-9.43v17.5Z" fill="#fff"/></svg>`;
	const caseIcon = `<svg width="50" height="67" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="m22.305 65.183-.002-.003c-3.39-4.202-8.855-11.392-13.468-18.957C6.528 42.441 4.44 38.575 2.93 34.95 1.416 31.314.5 27.959.5 25.19.5 11.55 11.472.5 25 .5c13.522 0 24.5 11.05 24.5 24.69 0 2.77-.923 6.123-2.446 9.758-1.52 3.626-3.619 7.492-5.933 11.275-4.627 7.564-10.091 14.755-13.426 18.96a3.416 3.416 0 0 1-5.39 0Z" fill="#faeff4" stroke="#C62774"/><path d="M35.32 41H14.68a.673.673 0 0 1-.48-.202.7.7 0 0 1-.2-.49V21.42a.7.7 0 0 1 .2-.49c.127-.13.3-.204.48-.204h4.76v-9.033a.705.705 0 0 1 .277-.56.674.674 0 0 1 .606-.103l15.2 4.82a.68.68 0 0 1 .344.252c.086.12.132.263.132.41v23.794a.7.7 0 0 1-.199.49.673.673 0 0 1-.48.204Zm-9.17-1.386h8.49V17.025l-13.839-4.391v8.093h4.669c.18 0 .353.073.48.203a.7.7 0 0 1 .2.49v18.194Zm-10.79 0h9.43v-17.5h-9.43v17.5Z" fill="#C62774"/></svg>`;

	let icon;

	if (args.activeIcon) {
		icon = type === 'case' ? caseIconActive : officeIconActive;
	} else {
		icon = type === 'case' ? caseIcon : officeIcon;
	}

	return {
		url: 'data:image/svg+xml;charset=utf-8,' + encodeURIComponent(icon),
		anchor: new window.google.maps.Point(25, 67), // Center bottom. SVG Icon is 50x67
	};
}

export function getActiveMapIcon(type) {
	return getMapIcon(type, { activeIcon: true });
}

export function getMapContentHTML(location, hideReadMore = false) {
	const title = location?.title;
	const permalink = location?.permalink;

	let contentHtml = '';

	if (title || permalink) {
		contentHtml += '<div class="m-infowindow__content">';

		if (title) {
			if (!hideReadMore && permalink) {
				contentHtml += `<h2 class="m-infowindow__title"><a href="${permalink}">${title}</a></h2>`;
			} else {
				contentHtml += `<h2 class="m-infowindow__title">${title}</h2>`;
			}
		}

		if (!hideReadMore && permalink) {
			contentHtml += `<a class="m-infowindow__permalink" href="${permalink}">Läs mer</a>`;
		}

		contentHtml += '</div>';
	}

	return contentHtml;
}

export class CustomClusterRenderer {
	render({ count, position }, stats) {
		// change color if this cluster has more markers than the mean cluster
		const color = count > Math.max(10, stats.clusters.markers.mean) ? '#c62774' : '#c62774';

		// create svg url with fill color
		const svg = window.btoa(`
		<svg fill="${color}" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 240 240">
			<circle cx="120" cy="120" opacity=".6" r="70" />
			<circle cx="120" cy="120" opacity=".3" r="90" />
			<circle cx="120" cy="120" opacity=".2" r="110" />
		</svg>`);

		// create marker using svg icon
		return new window.google.maps.Marker({
			position,
			icon: {
				url: `data:image/svg+xml;base64,${svg}`,
				scaledSize: new window.google.maps.Size(45, 45),
			},
			label: {
				text: String(count),
				color: 'rgba(255,255,255,0.9)',
				fontSize: '12px',
			},
			title: `Cluster of ${count} markers`,
			// adjust zIndex to be above other markers
			zIndex: Number(window.google.maps.Marker.MAX_ZINDEX) + count,
		});
	}
}
